<template>
  <div class="mt-1.5 mb-3" :class="{ 'px-6': !isSmView }">
    <v2-accordion-item inverted :isActive="fileApprovalVersion.isLatestVersion" noHover>
      <template v-slot:label>
        <div class="w-full">
          <div>
            <span>{{ $t("fileapproval.labels.version") }} {{ fileApprovalVersion.version }}:</span>
            <span class="ml-2" :class="'text-v2-' + statusColor">{{ statusText }}</span>
            <span
              v-if="fileApprovalVersion.isLatestVersion && isStatusPending && !hasSeen"
              class="text-sm mx-2 text-gray-500"
            >
              |</span
            >
            <span
              v-if="fileApprovalVersion.isLatestVersion && isStatusPending && !hasSeen"
              class="text-sm text-gray-500"
              >{{ $t("fileapproval.labels.customerHasFilesNotSeen") }}</span
            >
          </div>
          <div class="text-sm text-gray-500">
            {{ latestEventMessage }}
            {{ $h.formatDateTime(fileApprovalVersion.updated_at, timezone) }}
          </div>
        </div>
      </template>
      <template v-slot:box>
        <v2-row
          noSpace
          v-if="isStatusRejected && fileApprovalVersion.client_comment && fileApprovalVersion.client_comment.length > 0"
        >
          <v2-col col12>
            <div class="flex items-center">
              <!-- eslint-disable max-len -->
              <div
                class="flex justify-center items-center rounded-full w-8 h-8 border border-v2-black mr-2 bg-v2-blue-light"
              >
                <!-- eslint-enable max-len -->
                <v2-icon :data="require('@icon/Icon=user.svg')"></v2-icon>
              </div>
              <span class="text-sm text-gray-500">{{
                $t("specialCharacters.quotes.opening") +
                fileApprovalVersion.client_comment +
                $t("specialCharacters.quotes.closing")
              }}</span>
            </div>
          </v2-col>
        </v2-row>
        <v2-row noSpace>
          <v2-col col12>
            <div class="mb-3">
              <span>{{ $t("fileapproval.labels.versionDescription") }}</span>
              <div v-if="!isStatusDraft" class="text-sm text-gray-500">
                {{ fileApprovalVersion.notes }}
              </div>
              <v2-textarea v-if="isStatusDraft && fileApprovalVersion.isLatestVersion" v-model="notes"></v2-textarea>
              <div class="w-full flex justify-end">
                <v2-button
                  v-if="isStatusDraft && fileApprovalVersion.isLatestVersion"
                  class="mx-1 mt-4"
                  outline
                  primary
                  sm
                  :active="canSaveVersion"
                  @click.native="saveFileApprovalNotes()"
                >
                  {{ $t("fileapproval.buttons.save") }}
                </v2-button>
              </div>
            </div>
          </v2-col>
        </v2-row>
        <v2-row noSpace>
          <v2-col col12 noSpace>
            <span>{{ $t("fileapproval.labels.versionFiles") }}</span>
          </v2-col>
          <v2-col col5 v-if="isStatusDraft && fileApprovalVersion.isLatestVersion">
            <div
              :class="['v2-uppy-upload mt-0 h-full bo-filelist']"
              :id="'trigger-envivo-upload-box-' + fileApprovalVersion.uid.replace('.', '-')"
            ></div>
            <v2-uppy-upload
              :id="'fileapproval-version-fileupload' + fileApprovalVersion.uid.replace('.', '-')"
              :locale="locale"
              :target="'#trigger-envivo-upload-box-' + fileApprovalVersion.uid.replace('.', '-')"
              :inline="true"
              :create-file="createApprovalFileAttachments"
              :uploadConfig="uploadConfig"
              @completed="fileApprovalAttachmentCompleted"
            ></v2-uppy-upload>
          </v2-col>
          <v2-col noSpace :col7="isStatusDraft && fileApprovalVersion.isLatestVersion" :col12="!isStatusDraft">
            <div v-if="fileApprovalVersion.approval_files && fileApprovalVersion.approval_files.length > 0">
              <div
                class="mb-4 rounded-lg bg-v2-blue-light px-4 py-2"
                v-for="fileApprovalAttachmentFile in fileApprovalVersion.approval_files"
                :key="fileApprovalAttachmentFile.uid"
              >
                <file-approval-file-list-item
                  :fileApprovalAttachmentFile="fileApprovalAttachmentFile"
                  :timezone="timezone"
                  :projectUid="projectUid"
                  :fileApprovalUid="fileApprovalUid"
                  :fileApprovalVersion="fileApprovalVersion"
                  :isRemovable="isRemovable"
                ></file-approval-file-list-item>
              </div>
            </div>

            <div
              v-if="!fileApprovalVersion.approval_files || fileApprovalVersion.approval_files.length == 0"
              class="text-center text-gray-500"
              :class="[isSmView ? 'my-8' : 'mt-28']"
            >
              {{ $t("fileapproval.labels.noFilesInApprovalVersion") }}
            </div>
          </v2-col>
        </v2-row>
        <v2-row v-if="isStatusDraft && fileApprovalVersion.isLatestVersion">
          <v2-col col12 noSpace>
            <div class="w-full flex flex-wrap justify-end">
              <v2-button
                class="mx-1"
                :class="{ 'mb-4': isSmView }"
                primary
                :active="canPublishVersion"
                :fullWidth="isSmView"
                @click.native="saveAndPublishFileApprovalVersion()"
              >
                {{ $t("fileapproval.buttons.saveAndPublish") }}
              </v2-button>
              <v2-button
                :active="fileApprovalVersion.approval_files && fileApprovalVersion.approval_files == 0"
                class="mx-1"
                danger
                :fullWidth="isSmView"
                @click.native="deleteVersion()"
              >
                {{ $t("fileapproval.buttons.deleteVersion") }}
              </v2-button>
            </div>
          </v2-col>
        </v2-row>
        <v2-row v-if="isStatusPending && fileApprovalVersion.isLatestVersion">
          <v2-col col12 noSpace>
            <div class="w-full flex justify-end">
              <v2-button class="mx-1" primary @click.native="revokeVersion()">
                {{ $t("fileapproval.buttons.revokeVersion") }}
              </v2-button>
            </div>
          </v2-col>
        </v2-row>
      </template>
    </v2-accordion-item>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import FileApprovalFileListItem from "./FileApprovalFileListItem";
import globalMixin from "@/mixins/global.js";

export default {
  name: "FileApprovalVersion",
  mixins: [globalMixin],
  props: {
    projectUid: {
      type: String,
      required: true,
    },
    fileApprovalUid: {
      type: String,
      required: true,
    },
    fileApprovalVersion: {
      type: Object,
      required: true,
    },
    timezone: {
      type: String,
      required: true,
    },
    locale: {
      type: String,
      required: true,
    },
  },
  components: {
    FileApprovalFileListItem,
  },
  data() {
    return {
      notes: "",
      uploadConfig: {
        max_files: -1,
      },
    };
  },
  created() {},
  mounted() {
    let vm = this;

    // TODO: add additional functionality to get a preview of the image
    if (vm.fileApprovalVersion) vm.notes = vm.fileApprovalVersion.notes;
    if (vm.fileApprovalVersion && vm.fileApprovalVersion.approval_files.length > 0) {
      vm.fileApprovalVersion.approval_files.forEach(function (attachmentFile) {
        attachmentFile.hasPreviewUrl = false;
        attachmentFile.previewUrl = "";
      });
    }
  },
  destroyed() {},
  methods: {
    ...mapActions([
      "fileApprovalApiRequest",
      "createApprovalFileAttachmentFilesRequest",
      "updateFileApprovalVersionAttachmentRelation",
    ]),

    deleteVersion() {
      let vm = this;

      if (vm.fileApprovalVersion.approval_files && vm.fileApprovalVersion.approval_files == 0) {
        let payload = {
          apiMethod: "fileApprovalVersionDelete",
          params: {
            project: vm.projectUid,
            fileApproval: vm.fileApprovalUid,
            fileApprovalVersion: vm.fileApprovalVersion.uid,
          },
        };

        vm.fileApprovalApiRequest(payload)
          .then(() => {
            vm.$snotify.success(vm.$t("fileapproval.toasts.deleteVersion.success"), vm.$t("toasts.success"), {
              icon: vm.$successIcon,
            });
            console.log("file approval version deleted");
            vm.$eventbus.$emit("fileApprovalListReloadEvent");
          })
          .catch((err) => {
            vm.$snotify.error(vm.$t("fileapproval.toasts.deleteVersion.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
            console.log("error: ", err);
          });
      }
    },

    createApprovalFileAttachments(payload) {
      let vm = this;

      payload.params.project = vm.projectUid;

      return vm.createApprovalFileAttachmentFilesRequest(payload);
    },

    fileApprovalAttachmentCompleted(responseData) {
      let vm = this;
      let fileUid = responseData.uid;

      vm.createFileApprovalAttachmentRelation(fileUid);
    },

    createFileApprovalAttachmentRelation(fileUid) {
      let vm = this;

      let payloadData = {
        add_to_version: vm.fileApprovalVersion.uid,
      };

      vm.updateFileApprovalAttachmentRelation(fileUid, payloadData);
    },

    updateFileApprovalAttachmentRelation(fileUid, payloadData, deleteFile = false) {
      let vm = this;

      const payload = {
        params: {
          project: vm.projectUid,
          file: fileUid,
        },
        data: payloadData,
      };

      vm.updateFileApprovalVersionAttachmentRelation(payload)
        .then(() => {
          if (deleteFile) {
            vm.$snotify.success(vm.$t("fileapproval.toasts.deleteFile.success"), vm.$t("toasts.success"), {
              icon: vm.$successIcon,
            });
          } else {
            vm.$snotify.success(vm.$t("fileapproval.toasts.uploadFile.success"), vm.$t("toasts.success"), {
              icon: vm.$successIcon,
            });
          }

          vm.$eventbus.$emit("fileApprovalListReloadEvent");
        })
        .catch((err) => {
          if (deleteFile) {
            vm.$snotify.error(vm.$t("fileapproval.toasts.deleteFile.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          } else {
            vm.$snotify.error(vm.$t("fileapproval.toasts.uploadFile.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          }

          console.log("error: ", err);
        });
    },

    revokeVersion() {
      let vm = this;

      const payload = {
        apiMethod: "fileApprovalVersionUpdate",
        params: {
          project: vm.projectUid,
          fileApproval: vm.fileApprovalUid,
          fileApprovalVersion: vm.fileApprovalVersion.uid,
        },
        data: {
          notes: vm.fileApprovalVersion.notes,
          revoked: true,
        },
      };

      vm.fileApprovalApiRequest(payload)
        .then((fileApprovalVersion) => {
          vm.$snotify.success(vm.$t("fileapproval.toasts.revokeVersion.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
          console.log("update file approval version", fileApprovalVersion);
          vm.$emit("versionRevoked");
        })
        .catch((err) => {
          vm.$snotify.error(vm.$t("fileapproval.toasts.revokeVersion.error"), vm.$t("toasts.error"), {
            timeout: 0,
            icon: vm.$errorIcon,
          });
          console.log("error: ", err);
        });
    },

    saveFileApprovalNotes() {
      let vm = this;

      if (!vm.canSaveVersion) {
        return;
      }

      let payload = {
        apiMethod: "fileApprovalVersionUpdate",
        params: {
          project: vm.projectUid,
          fileApproval: vm.fileApprovalUid,
          fileApprovalVersion: vm.fileApprovalVersion.uid,
        },
        data: {
          notes: vm.notes,
        },
      };

      vm.fileApprovalApiRequest(payload)
        .then((fileApprovalVersion) => {
          vm.$snotify.success(vm.$t("fileapproval.toasts.saveNotes.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
          console.log("update file approval version", fileApprovalVersion);
        })
        .catch((err) => {
          vm.$snotify.error(vm.$t("fileapproval.toasts.saveNotes.error"), vm.$t("toasts.error"), {
            timeout: 0,
            icon: vm.$errorIcon,
          });
          console.log("error: ", err);
        });
    },

    saveFileApprovalVersion(publishVersion) {
      let vm = this;

      if (!vm.canSaveVersion) {
        return;
      }

      let payload = {
        apiMethod: "fileApprovalVersionUpdate",
        params: {
          project: vm.projectUid,
          fileApproval: vm.fileApprovalUid,
          fileApprovalVersion: vm.fileApprovalVersion.uid,
        },
        data: {
          notes: vm.notes,
        },
      };

      if (publishVersion) {
        payload.data.issued = true;
      }

      vm.fileApprovalApiRequest(payload)
        .then((fileApprovalVersion) => {
          vm.$snotify.success(vm.$t("fileapproval.toasts.publishVersion.success"), vm.$t("toasts.success"), {
            icon: vm.$successIcon,
          });
          console.log("update file approval version", fileApprovalVersion);
          vm.$eventbus.$emit("fileApprovalListReloadEvent");
        })
        .catch((err) => {
          vm.$snotify.error(vm.$t("fileapproval.toasts.publishVersion.error"), vm.$t("toasts.error"), {
            timeout: 0,
            icon: vm.$errorIcon,
          });
          console.log("error: ", err);
        });
    },

    saveAndPublishFileApprovalVersion() {
      console.log("saveAndPublishFileApprovalVersion");

      let vm = this;

      if (!vm.canPublishVersion) {
        return;
      }

      vm.saveFileApprovalVersion(true);
    },

    getApprovalFileVersionAttachmentPreviewUrl(attachmentFile, attachmentIdx) {
      var vm = this;

      const payload = {
        apiMethod: "fileApprovalAttachmentPreviewUrl",
        params: {
          project: vm.projectUid,
          file: attachmentFile.uid,
          rendermethod: "resize",
          page: 0,
          width: 200,
          height: 150,
          redirect: true,
        },
        data: {
          notes: vm.fileApprovalVersion.notes,
          revoked: true,
        },
      };

      vm.fileApprovalApiRequest(payload)
        .then((res) => {
          console.log("fetch preview url: ", res);

          vm.fileApprovalVersion.approval_files[attachmentIdx].previewUrl = res.redirect;
          vm.fileApprovalVersion.approval_files[attachmentIdx].hasPreviewUrl = true;
        })
        .catch((err) => {
          console.log("error: ", err);
        });
    },
  },
  computed: {
    hasSeen() {
      let vm = this;

      let hasSeenFiles = false;

      vm.fileApprovalVersion.approval_files.forEach(function (attachmentFile) {
        if (attachmentFile.first_seen_at) {
          hasSeenFiles = true;
        }
        if (attachmentFile.first_download_at) {
          hasSeenFiles = true;
        }
      });

      return hasSeenFiles;
    },

    canSaveVersion() {
      let vm = this;

      if (vm.notes.length > 0) {
        return true;
      }
      return false;
    },

    canPublishVersion() {
      let vm = this;

      if (
        vm.notes.length > 0 &&
        vm.fileApprovalVersion.approval_files &&
        vm.fileApprovalVersion.approval_files.length > 0
      ) {
        return true;
      }
      return false;
    },

    statusText() {
      let vm = this;

      if (vm.isStatusDraft) {
        return vm.$t("fileapproval.labels.statusTextNotPublished");
      } else if (vm.isStatusPending) {
        return vm.$t("fileapproval.labels.statusTextPending");
      } else if (vm.isStatusApproved) {
        return vm.$t("fileapproval.labels.statusTextApproved");
      } else if (vm.isStatusRejected) {
        return vm.$t("fileapproval.labels.statusTextRejected");
      } else if (vm.isStatusRevoked) {
        return vm.$t("fileapproval.labels.statusTextRevoked");
      } else {
        return "Unknown state";
      }
    },

    statusColor() {
      if (this.isStatusDraft || this.isStatusRevoked) {
        return "grey-light";
      } else if (this.isStatusPending) {
        return "status-yellow";
      } else if (this.isStatusApproved) {
        return "status-green";
      } else if (this.isStatusRejected) {
        return "status-red";
      } else {
        return "black";
      }
    },

    latestEventMessage() {
      let vm = this;

      if (this.isStatusDraft) {
        return vm.$t("fileapproval.labels.draftTimestampLabel");
      } else if (this.isStatusPending) {
        return vm.$t("fileapproval.labels.pendingTimestampLabel");
      } else if (this.isStatusApproved) {
        return vm.$t("fileapproval.labels.approvedTimestampLabel");
      } else if (this.isStatusRejected) {
        return vm.$t("fileapproval.labels.rejectedTimestampLabel");
      } else if (this.isStatusRevoked) {
        return vm.$t("fileapproval.labels.revokeTimestampLabel");
      } else {
        return "Unknown state";
      }
    },

    isStatusDraft() {
      if (!this.fileApprovalVersion.issued_at) {
        return true;
      }
      return false;
    },
    isStatusPending() {
      if (
        this.fileApprovalVersion.issued_at &&
        !this.fileApprovalVersion.revoked_at &&
        !this.fileApprovalVersion.rejected_at &&
        !this.fileApprovalVersion.approved_at
      ) {
        return true;
      }
      return false;
    },
    isStatusRevoked() {
      if (
        this.fileApprovalVersion.revoked_at &&
        !this.fileApprovalVersion.rejected_at &&
        !this.fileApprovalVersion.approved_at
      ) {
        return true;
      }
      return false;
    },
    isStatusApproved() {
      if (this.fileApprovalVersion.approved_at) {
        return true;
      }
      return false;
    },
    isStatusRejected() {
      if (this.fileApprovalVersion.rejected_at) {
        return true;
      }
      return false;
    },
    isRemovable() {
      return this.isStatusDraft;
    },
  },
  watch: {},
};
</script>
